import { type AdData, adDestination } from "@adland/data";
import { getAdDescription, getAdIcon, getAdTitle } from "@adland/react/core";

/**
 * Part names exposed for `::part()` styling in inline (shadow DOM) mode.
 *
 * `badge` was here and is gone. It rendered the ad's TYPE — "Link", "Token" —
 * which tells a reader nothing they want: they can see it is a link by looking
 * at it, and the word cost a chunk of a row that is already one line of title
 * and one of description. `label` stays, because "Ad" is a disclosure rather
 * than a description.
 */
export const AD_PARTS = [
  "card",
  "media",
  "body",
  "title",
  "description",
  "label",
] as const;

/** Escape text for safe interpolation into HTML element content. */
export function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}

/** Escape a value for use inside a double-quoted HTML attribute. */
export function escapeAttr(value: string): string {
  return value.replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

/**
 * Default ad-card styles. Every visual is driven by a `--adland-*` custom
 * property with a sensible fallback, so a publisher can re-theme the card
 * without overriding a single rule. Custom properties inherit through the
 * shadow boundary (inline mode) and are copied into the iframe (isolated mode).
 */
export const BASE_STYLES = `
:host { display: block; }
* { box-sizing: border-box; }
.adland-card {
  position: relative;
  display: flex;
  gap: var(--adland-gap, 12px);
  align-items: center;
  padding: var(--adland-padding, 12px);
  border: var(--adland-border-width, 1px) solid var(--adland-border, #e5e7eb);
  border-radius: var(--adland-radius, 12px);
  background: var(--adland-bg, #ffffff);
  color: var(--adland-fg, #111827);
  font-family: var(--adland-font, system-ui, -apple-system, sans-serif);
  cursor: pointer;
  text-decoration: none;
  /* Resting shadow, not just a hover one. Only --adland-shadow-hover existed,
     which quietly ruled out every style whose card is raised at rest: a hard
     offset shadow is most of what makes a neo-brutalist card look like one, and
     there was no way to ask for it. Defaults to none, so nothing moves. */
  box-shadow: var(--adland-shadow, none);
  transition: var(
    --adland-transition,
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease
  );
}
.adland-card:hover {
  border-color: var(--adland-border-hover, #d1d5db);
  box-shadow: var(--adland-shadow-hover, 0 1px 3px rgba(0, 0, 0, 0.08));
  /* The other half of a raised card: shrink the shadow AND move the card into
     the space it vacated, or the card appears to grow rather than press. No
     default, so a theme that sets neither is untouched. */
  transform: var(--adland-transform-hover, none);
}
.adland-media {
  width: var(--adland-media-size, 44px);
  height: var(--adland-media-size, 44px);
  border-radius: var(--adland-media-radius, 8px);
  object-fit: cover;
  flex: none;
  background: var(--adland-media-bg, #f3f4f6);
  border: var(--adland-media-border, none);
}
.adland-body { min-width: 0; flex: 1; }
.adland-title {
  margin: 0;
  font-size: var(--adland-title-size, 14px);
  font-weight: var(--adland-title-weight, 600);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adland-description {
  margin: 2px 0 0;
  font-size: var(--adland-description-size, 12px);
  color: var(--adland-muted, #6b7280);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adland-label {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: var(--adland-label-size, 9px);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--adland-label, #9ca3af);
}
.adland-empty { justify-content: center; color: var(--adland-muted, #6b7280); }
`;

/** Inner card markup (parts + classes) for a loaded ad. */
export function cardMarkup(ad: AdData): string {
  /*
   * The icon, not the image — this card is a compact row.
   *
   * `@adland/react` exposes both because an ad carries both, and which is right
   * depends on the shape of the space rather than on the ad: `getAdImage`
   * prefers a wide cover, `getAdIcon` prefers a square mark. `.adland-media` is
   * square by construction — `--adland-media-size` sets its width AND its
   * height — so it is unambiguously the second case.
   *
   * It was asking for the image, which for a link ad meant an 840x560 Open
   * Graph cover cropped into a 44px box: the middle of somebody's banner where
   * their favicon belongs, and nothing about it looking like a mistake rather
   * than a bad picture. Both getters fall back to the other, so an ad with only
   * one still renders.
   */
  const image = getAdIcon(ad);
  const title = getAdTitle(ad);
  const description = getAdDescription(ad);

  return `<div part="card" class="adland-card">${
    image
      ? `<img part="media" class="adland-media" src="${escapeAttr(image)}" alt="" loading="lazy" />`
      : ""
  }<div part="body" class="adland-body">${
    title ? `<p part="title" class="adland-title">${escapeHtml(title)}</p>` : ""
  }${
    description
      ? `<p part="description" class="adland-description">${escapeHtml(description)}</p>`
      : ""
  }</div><span part="label" class="adland-label">Ad</span></div>`;
}

/** Inner card markup for an empty slot (the "Your ad here" CTA). */
export function emptyMarkup(): string {
  return `<div part="card" class="adland-card adland-empty"><span part="description" class="adland-description">Your ad here</span><span part="label" class="adland-label">Ad</span></div>`;
}

/**
 * Where a click on this ad should go, on the open web.
 *
 * Delegates to `adDestination` in `@adland/data`, which is what
 * `WebActionAdapter` in `@adland/react` has always used. This was a second,
 * shorter implementation that handled `link` and `miniapp` and returned null
 * for everything else — so in the default isolated mode a token, cast, tweet
 * or profile ad rendered no anchor and a click on it did nothing at all, while
 * the same ad in inline or light mode worked, because those route through the
 * adapter instead. One destination function, one behaviour, six ad types.
 */
export function adWebUrl(ad: AdData): string | null {
  return adDestination(ad);
}
